import { announceSignInNeeded, NeedsSignIn } from '../auth/session.tsx';

export class ApiError extends Error {
  status: number;
  hint: string | undefined;
  constructor(status: number, message: string, hint?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.hint = hint;
  }
}

export type TokenSource = () => Promise<string>;

/**
 * Every request the app makes. Same origin, always under /api.
 *
 * A 401 means the token was rejected rather than absent — the session ran out
 * between the check and the call. Surfacing it as NeedsSignIn lets the shell
 * redirect once, rather than each screen inventing its own handling.
 */
export async function apiFetch<T>(
  path: string,
  getToken: TokenSource,
  init?: RequestInit,
): Promise<T> {
  const token = await getToken();
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      authorization: `Bearer ${token}`,
      accept: 'application/json',
    },
  });

  // Told once, to the shell, which swaps to the sign-in screen. Still thrown,
  // so the caller stops — but no screen has to render it, and none should:
  // "your session ended" and "the server is unreachable" are different facts
  // and were being shown together.
  if (res.status === 401) {
    announceSignInNeeded('rejected');
    throw new NeedsSignIn();
  }

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let hint: string | undefined;
    try {
      const body = (await res.json()) as { error?: string; hint?: string };
      if (body.error) message = body.error;
      hint = body.hint;
    } catch {
      // Non-JSON error body; the status line is all we have.
    }
    throw new ApiError(res.status, message, hint);
  }

  return (await res.json()) as T;
}

/** Unauthenticated, and the only call made before there is a session. */
export async function fetchAuthConfig<T>(): Promise<T> {
  const res = await fetch('/api/auth/config', { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`The server did not report an auth mode (${res.status})`);
  return (await res.json()) as T;
}
